import { useCallback, useEffect, useState } from 'react';

export type Theme = 'dark' | 'light' | 'system';

const KEY = 'studio.theme';

const read = (): Theme => {
  try {
    const saved = localStorage.getItem(KEY);
    return saved === 'dark' || saved === 'light' ? saved : 'system';
  } catch {
    // Private windows and blocked site data both throw on access.
    return 'system';
  }
};

/**
 * Which room the studio is lit as.
 *
 * "System" is the default and is represented by the *absence* of the attribute,
 * so the stylesheet's `prefers-color-scheme` block does the work and the page
 * follows the machine without JavaScript having to watch anything.
 */
export function useTheme(): { theme: Theme; setTheme: (next: Theme) => void } {
  const [theme, setState] = useState<Theme>(read);

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'system') root.removeAttribute('data-theme');
    else root.setAttribute('data-theme', theme);

    try {
      if (theme === 'system') localStorage.removeItem(KEY);
      else localStorage.setItem(KEY, theme);
    } catch {
      // A preference that cannot be saved still applies for this session.
    }
  }, [theme]);

  return { theme, setTheme: useCallback((next: Theme) => setState(next), []) };
}

/** Applied before React mounts, so the first paint is already the right room. */
export function applyStoredTheme(): void {
  const theme = read();
  if (theme !== 'system') document.documentElement.setAttribute('data-theme', theme);
}
